import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import { useMeCore } from '../../../../core/modules/me';
import {
  extractApiErrorMessage,
  stockComparisonService,
  StockComparisonConfig,
  StockComparisonUploadResponse,
  StockComparisonResultItem,
  StockComparisonResultStatus,
} from '../../../../core/modules/stockComparison/services/api';
import StockComparisonUploadDialog from './StockComparisonUploadDialog';
import StockComparisonTrendChart from './StockComparisonTrendChart';
import StockComparisonMovementsDialog from './StockComparisonMovementsDialog';

const STATUS_LABEL: Record<StockComparisonResultStatus, string> = {
  matched: 'Dentro del umbral',
  deviated: 'Desviado',
  missing_secondary: 'Sin dato del secundario',
  missing_primary: 'Sin dato del primario',
};

const STATUS_COLOR: Record<StockComparisonResultStatus, 'success' | 'error' | 'default' | 'warning'> = {
  matched: 'success',
  deviated: 'error',
  missing_secondary: 'warning',
  missing_primary: 'warning',
};

// Los campos numericos viajan como string (Decimal serializado) -- estos
// helpers los formatean para mostrar, sin perder el "N/A" cuando vienen null
// (por ejemplo deviationValue cuando el producto no tiene costo cargado, o
// primaryQty cuando el SKU no existe en el primario -- ver status
// missing_primary).
const formatQty = (value: string | null): string => (value === null ? '—' : Number(value).toLocaleString('es-CL'));
const formatPct = (value: string | null): string => (value === null ? '—' : `${Number(value).toLocaleString('es-CL')}%`);
const formatMoney = (value: string | null): string =>
  value === null ? '—' : `$${Number(value).toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;

const StockComparisonPage = () => {
  const { id: companyIdParam } = useParams<{ id: string }>();
  const companyId = Number(companyIdParam);
  const { user } = useMeCore();

  // Configurar (umbral y nombre) es de company_admin/superadmin -- Usuario
  // solo ve el resto de la pantalla (subir archivo y el resultado). Pedido
  // explicito de Seba sobre la gobernanza de permisos del modulo.
  const canConfigure = !!user?.is_superuser || user?.role === 'Administrador de empresa';

  const [config, setConfig] = useState<StockComparisonConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [labelInput, setLabelInput] = useState('');
  const [thresholdInput, setThresholdInput] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [lastResult, setLastResult] = useState<StockComparisonUploadResponse | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [diagnosisTarget, setDiagnosisTarget] = useState<StockComparisonResultItem | null>(null);

  const loadConfig = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await stockComparisonService.getConfig(companyId);
      setConfig(data);
      setLabelInput(data.secondarySystemLabel ?? '');
      setThresholdInput(String(data.varianceThresholdPct));
    } catch (err) {
      setError('No se pudo cargar la configuración de Stock Comparison.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const configIsPristine =
    !!config &&
    labelInput === (config.secondarySystemLabel ?? '') &&
    thresholdInput === String(config.varianceThresholdPct);

  const handleSaveConfig = async () => {
    if (!companyId) return;
    const thresholdNumber = Number(thresholdInput);
    if (Number.isNaN(thresholdNumber) || thresholdNumber < 0) {
      setError('El umbral debe ser un número mayor o igual a 0.');
      return;
    }
    setSavingConfig(true);
    setError(null);
    setInfo(null);
    try {
      const result = await stockComparisonService.updateConfig(companyId, {
        secondarySystemLabel: labelInput.trim() || null,
        varianceThresholdPct: thresholdNumber,
      });
      setConfig(result);
      setLabelInput(result.secondarySystemLabel ?? '');
      setThresholdInput(String(result.varianceThresholdPct));
      setInfo('Configuración guardada.');
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'No se pudo guardar la configuración.'));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleUploaded = (result: StockComparisonUploadResponse) => {
    setLastResult(result);
    setInfo(null);
    setError(null);
  };

  if (loading) {
    return (
      <Box className="container" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="container" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Stock Comparison
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Compara el stock de un sistema secundario (por ejemplo, el ERP de la empresa) contra el
        stock que Inventaria tiene en vivo ahora mismo. Sube un archivo con el stock del
        secundario tal como está en este momento -- la comparación se calcula al instante contra el
        stock actual, no contra un dato histórico, así que lo mejor es exportar y subir el
        archivo el mismo día.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      {canConfigure && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Configuración
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              El umbral define a partir de qué % de diferencia una fila queda marcada como
              "Desviado". El nombre del sistema secundario es solo una etiqueta para esta
              pantalla (ej. "SAP", "Defontana") -- no afecta el cálculo.
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <TextField
                  label="Nombre del sistema secundario"
                  placeholder="Ej: SAP"
                  fullWidth
                  value={labelInput}
                  onChange={(e) => setLabelInput(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Umbral de desviación (%)"
                  type="number"
                  fullWidth
                  value={thresholdInput}
                  onChange={(e) => setThresholdInput(e.target.value)}
                  inputProps={{ min: 0, step: 0.5 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={savingConfig || configIsPristine}
                  onClick={handleSaveConfig}
                >
                  {savingConfig ? <CircularProgress size={20} /> : 'Guardar configuración'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Cargar stock del sistema secundario
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            El archivo necesita, para cada fila: código de producto, bodega, y la cantidad que
            reporta el sistema secundario ahora mismo. La fecha es opcional -- si no la indicas,
            se asume la de hoy. En el siguiente paso vas a poder mapear las columnas de tu
            archivo a estos campos, aunque no se llamen igual.
          </Typography>
          <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => setUploadDialogOpen(true)}>
            Subir archivo
          </Button>
        </CardContent>
      </Card>

      <Card>
        <Tabs value={activeTab} onChange={(_e, value) => setActiveTab(value)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Resultados de la última carga" />
          <Tab label="Evolución diaria" />
        </Tabs>
        <CardContent>
          {activeTab === 0 && (
            lastResult ? (
              <>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Resultado de la carga ({lastResult.comparisonDate})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  <Chip label={`${lastResult.totalRows} filas procesadas`} />
                  <Chip label={`${lastResult.matchedCount} dentro del umbral`} color="success" />
                  <Chip label={`${lastResult.deviatedCount} desviados`} color="error" />
                  <Chip label={`${lastResult.missingPrimaryCount} sin dato del primario`} color="warning" />
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Producto</TableCell>
                        <TableCell>Bodega</TableCell>
                        <TableCell align="right">Primario</TableCell>
                        <TableCell align="right">Secundario</TableCell>
                        <TableCell align="right">Diferencia</TableCell>
                        <TableCell align="right">%</TableCell>
                        <TableCell align="right">Impacto ($)</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell align="center">Diagnóstico</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lastResult.results.map((row: StockComparisonResultItem, idx: number) => (
                        <TableRow key={`${row.productSku}-${row.warehouseLabel}-${idx}`}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {row.productName ?? row.productSku}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.productSku}
                            </Typography>
                          </TableCell>
                          <TableCell>{row.warehouseLabel}</TableCell>
                          <TableCell align="right">{formatQty(row.primaryQty)}</TableCell>
                          <TableCell align="right">{formatQty(row.secondaryQty)}</TableCell>
                          <TableCell align="right">{formatQty(row.deviationQty)}</TableCell>
                          <TableCell align="right">{formatPct(row.deviationPct)}</TableCell>
                          <TableCell align="right">{formatMoney(row.deviationValue)}</TableCell>
                          <TableCell>
                            <Chip label={STATUS_LABEL[row.status]} color={STATUS_COLOR[row.status]} size="small" />
                          </TableCell>
                          <TableCell align="center">
                            {row.status !== 'matched' && (
                              <Tooltip title="Ver movimientos del sistema primario para ubicar la transacción del descuadre">
                                <span>
                                  <Button size="small" startIcon={<TroubleshootIcon />} onClick={() => setDiagnosisTarget(row)}>
                                    Diagnosticar
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Todavía no has subido ningún archivo en esta sesión. Sube uno para ver el resultado aquí.
              </Typography>
            )
          )}
          {activeTab === 1 && (
            <>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Evolución del % de desviación
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Un punto por cada día en que se cargó el archivo del sistema secundario, con el
                promedio y el máximo de desviación (en valor absoluto) de ese día.
              </Typography>
              <StockComparisonTrendChart companyId={companyId} />
            </>
          )}
        </CardContent>
      </Card>

      <StockComparisonUploadDialog
        isOpen={uploadDialogOpen}
        companyId={companyId}
        onClose={() => setUploadDialogOpen(false)}
        onUploaded={handleUploaded}
      />

      {diagnosisTarget && (
        <StockComparisonMovementsDialog
          isOpen={!!diagnosisTarget}
          companyId={companyId}
          productSku={diagnosisTarget.productSku}
          productName={diagnosisTarget.productName}
          warehouseLabel={diagnosisTarget.warehouseLabel}
          referenceDate={lastResult?.comparisonDate ?? ''}
          onClose={() => setDiagnosisTarget(null)}
        />
      )}
    </Box>
  );
};

export default StockComparisonPage;
